import { takeLatest, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';

// Services
import { fareBonusApi } from 'services/apiDigitalTicket/fareBonus';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Constants
import { ERROR_CODE_MAP } from 'constants/errorCodes';
import PATH from 'constants/basePath';
import { errorMessagersList } from 'helpers/errorMessagers';

import * as FareBonusActions from './actions';
import { FareBonusTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof FareBonusActions.listFareBonusRequest>): any {
  try {
    const { body } = payload;

    yield put(
      DialogsActions.setOpenLoading({
        title: 'Carregando alterações por período',
      }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(fareBonusApi.get, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        FareBonusActions.listFareBonusSuccess({
          fareBonusList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode = error.response && error.response.data.internalCode;
    const { message } =
      ERROR_CODE_MAP.get(internalCode) || ERROR_CODE_MAP.get('error.generic')!;

    console.error('fareBonus - listFareBonus - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
        status: error.response.status,
      }),
    );
    yield put(FareBonusActions.listFareBonusFailure());

    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof FareBonusActions.getFareBonusRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Carregando alteração por período',
      }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(fareBonusApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FareBonusActions.getFareBonusSuccess({
          fareBonusData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    console.error('fareBonus - getFareBonus - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Não foi possível listar alteração por período',
      }),
    );
    yield put(FareBonusActions.getFareBonusFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
  meta,
}: ReturnType<typeof FareBonusActions.filterFareBonusRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(fareBonusApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FareBonusActions.filterFareBonusSuccess({
          fareBonusList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
      meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fareBonus - filterFareBonus - error', error);
    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );
    yield put(FareBonusActions.filterFareBonusFailure());
    yield put(DialogsActions.setClose());
    meta.promise.reject(error);
  }
}

function* create({
  payload,
}: ReturnType<typeof FareBonusActions.createFareBonusRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Criando alteração por período' }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(fareBonusApi.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FareBonusActions.createFareBonusSuccess({
          fareBonusData: response.data,
        }),
      );

      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Alteração por período criada com sucesso',
        }),
      );

      yield put(
        FareBonusActions.listFareBonusRequest({
          body: {
            _includes: 'day,period,fare,carFare',
          },
        }),
      );

      yield put(push(PATH.CHANGES_BY_PERIOD.BASE_URL));
    }
  } catch (error) {
    const internalCode = error.response && error.response.data.internalCode;
    const { message } =
      ERROR_CODE_MAP.get(internalCode) || ERROR_CODE_MAP.get('error.generic')!;

    console.error('fareBonus - createFareBonus - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FareBonusActions.createFareBonusFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof FareBonusActions.updateFareBonusRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Editando alteração por período',
      }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(fareBonusApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FareBonusActions.updateFareBonusSuccess({
          fareBonusData: response.data,
        }),
      );

      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Alteração por período alterada com sucesso',
        }),
      );

      yield put(
        FareBonusActions.listFareBonusRequest({
          body: {
            _includes: 'day,period,fare,carFare',
          },
        }),
      );

      yield put(push(PATH.CHANGES_BY_PERIOD.BASE_URL));
    }
  } catch (error) {
    const internalCode = error.response && error.response.data.internalCode;
    const { message } =
      ERROR_CODE_MAP.get(internalCode) || ERROR_CODE_MAP.get('error.generic')!;

    console.error('fareBonus - updateFareBonus - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FareBonusActions.updateFareBonusFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof FareBonusActions.deleteFareBonusRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo produto' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(fareBonusApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Alteração por período excluída com sucesso',
        }),
      );

      yield put(DialogsActions.setClose());
      yield put(FareBonusActions.deleteFareBonusSuccess());
      yield put(FareBonusActions.listFareBonusRequest({}));
    }
  } catch (error) {
    const internalCode = error.response && error.response.data.internalCode;
    const { message } =
      ERROR_CODE_MAP.get(internalCode) || ERROR_CODE_MAP.get('error.generic')!;

    console.error('fareBonus - remove - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FareBonusActions.deleteFareBonusFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const fareBonusSagas = [
  takeLatest(FareBonusTypes.LIST_FARE_BONUS_REQUEST, list),
  takeLatest(FareBonusTypes.GET_FARE_BONUS_REQUEST, get),
  takeLatest(FareBonusTypes.FILTER_FARE_BONUS_REQUEST, filter),
  takeLatest(FareBonusTypes.CREATE_FARE_BONUS_REQUEST, create),
  takeLatest(FareBonusTypes.UPDATE_FARE_BONUS_REQUEST, update),
  takeLatest(FareBonusTypes.DELETE_FARE_BONUS_REQUEST, remove),
];
